/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { Grid, FormControl, InputLabel, Select, MenuItem, Tooltip } from "@mui/material";
import { getCellChartGroups, getCellChartByKey, getCellTechnologyFromNodeType } from "../Performance/Cell";
import { getChartGridSize } from "../Performance/chartGroupConfigs";
import { usePerformanceFilters, TimeRange } from "../Performance/PerformanceContext";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";

interface ICellKpisChartTabProps {
  siteName: string;
  cellName: string;
  nodeType: string;
  technology?: 'LTE' | 'NR' | 'SA';  // Optional override — if provided, skips getCellTechnologyFromNodeType
}

/**
 * CellKpisChartTab Component
 * 
 * Displays cell-level KPI charts using ECharts library.
 * This is an alternative to the Superset embedded dashboard.
 * 
 * Uses the cell chart registry for dynamic chart rendering.
 * Charts are automatically organized into groups (Accessibility, Retainability, etc.)
 * Charts are filtered by technology (LTE or NR) based on the node type.
 * 
 * Layout: 2 charts side-by-side, additional charts flow to bottom rows
 * 
 * @param siteName - The site/device serial number
 * @param cellName - The specific cell name
 * @param nodeType - The node type (SN, BBU, RCP, CU)
 */
const CellKpisChartTab: React.FC<ICellKpisChartTabProps> = ({ siteName, cellName, nodeType, technology: technologyOverride }) => {
  const [fullscreenChart, setFullscreenChart] = React.useState<string | null>(null);
  const [chartLayout, setChartLayout] = React.useState<'vertical' | 'horizontal'>('horizontal');
  
  // Use shared filter state from Performance context
  const { 
    timeRange, 
    truncate, 
    startDate, 
    endDate, 
    setTimeRange, 
    setTruncate, 
    setStartDate, 
    setEndDate,
    refreshInterval,
    setRefreshInterval,
    incrementRefreshTrigger
  } = usePerformanceFilters();

  const handleFullscreenToggle = (chartKey: string) => {
    setFullscreenChart(fullscreenChart === chartKey ? null : chartKey);
  };

  // Determine technology from node type — or use explicit override (e.g. 'SA' for the ECharts - SA tab)
  const technology = technologyOverride ?? getCellTechnologyFromNodeType(nodeType);

  // Get chart groups filtered by technology
  const chartGroups = getCellChartGroups(technology);

  // If a chart is in fullscreen mode, only show that chart
  if (fullscreenChart) {
    const chartConfig = getCellChartByKey(fullscreenChart, technology);
    if (!chartConfig) return null;

    const ChartComponent = chartConfig.component;
    
    return (
      <div className="w-full p-2">
        <ChartComponent
          onFullscreenToggle={() => handleFullscreenToggle(fullscreenChart)}
          isFullscreen={true}
          cellName={cellName}
        />
      </div>
    );
  }

  // Normal view with all charts organized by groups
  return (
    <div className="w-full p-2 bg-background">
      {/* Filters Section */}
      <div className="mb-6 flex gap-2 items-center flex-wrap justify-end">
        <FormControl size="small" sx={{ minWidth: 120, '& .MuiInputLabel-root': { color: 'var(--muted-foreground)' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' }, '& .MuiSelect-select': { color: 'var(--foreground)' }, '& .MuiSvgIcon-root': { color: 'var(--muted-foreground)' } }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => {
              const value = e.target.value as TimeRange;
              setTimeRange(value);
              if (value !== 'custom') {
                setStartDate(null);
                setEndDate(null);
              }
            }}
          >
            <MenuItem value="lastWeek">Last Week</MenuItem>
            <MenuItem value="lastMonth">Last Month</MenuItem>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>
        
        {timeRange === 'custom' && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    minWidth: 150,
                    '& .MuiInputBase-root': { backgroundColor: 'var(--background)' },
                    '& .MuiInputBase-input': { color: 'var(--foreground)' },
                    '& .MuiInputLabel-root': { color: 'var(--muted-foreground)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--foreground)' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' },
                    '& .MuiSvgIcon-root': { color: 'var(--muted-foreground)' },
                  }
                }
              }}
              maxDate={endDate || dayjs()}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    minWidth: 150,
                    '& .MuiInputBase-root': { backgroundColor: 'var(--background)' },
                    '& .MuiInputBase-input': { color: 'var(--foreground)' },
                    '& .MuiInputLabel-root': { color: 'var(--muted-foreground)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--foreground)' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' },
                    '& .MuiSvgIcon-root': { color: 'var(--muted-foreground)' },
                  }
                }
              }}
              minDate={startDate || undefined}
              maxDate={dayjs()}
            />
          </LocalizationProvider>
        )}
        
        <FormControl size="small" sx={{ minWidth: 120, '& .MuiInputLabel-root': { color: 'var(--muted-foreground)' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' }, '& .MuiSelect-select': { color: 'var(--foreground)' }, '& .MuiSvgIcon-root': { color: 'var(--muted-foreground)' } }}>
          <InputLabel>Interval</InputLabel>
          <Select
            value={truncate}
            label="Interval"
            onChange={(e) => setTruncate(e.target.value)}
          >
            <MenuItem value="minute">Minute</MenuItem>
            <MenuItem value="hour">Hour</MenuItem>
            <MenuItem value="day">Day</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 120, '& .MuiInputLabel-root': { color: 'var(--muted-foreground)' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' }, '& .MuiSelect-select': { color: 'var(--foreground)' }, '& .MuiSvgIcon-root': { color: 'var(--muted-foreground)' } }}>
          <InputLabel>Auto-Refresh</InputLabel>
          <Select
            value={refreshInterval}
            label="Auto-Refresh"
            onChange={(e) => setRefreshInterval(Number(e.target.value))}
          >
            <MenuItem value={0}>Never</MenuItem>
            <MenuItem value={600000}>10 minutes</MenuItem>
            <MenuItem value={900000}>15 minutes</MenuItem>
            <MenuItem value={1800000}>30 minutes</MenuItem>
          </Select>
        </FormControl>
        
        <Tooltip title="Refresh now">
          <button 
            onClick={() => incrementRefreshTrigger()}
            className="h-8 w-8 flex items-center justify-center rounded-md border border-input bg-background text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground transition-colors"
          >
            <RefreshIcon sx={{ fontSize: 16 }} />
          </button>
        </Tooltip>
        
        <Tooltip title={chartLayout === 'vertical' ? 'Switch to Horizontal Layout' : 'Switch to Vertical Layout'}>
          <button 
            onClick={() => setChartLayout(chartLayout === 'vertical' ? 'horizontal' : 'vertical')}
            className="h-8 w-8 flex items-center justify-center rounded-md border border-input bg-background text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground transition-colors"
          >
            {chartLayout === 'vertical' ? <ViewModuleIcon sx={{ fontSize: 16 }} /> : <ViewStreamIcon sx={{ fontSize: 16 }} />}
          </button>
        </Tooltip>
      </div>

      {/* Show message if no charts available for this technology */}
      {chartGroups.length === 0 && (
        <div className="text-center py-16">
          <h3 className="text-lg font-semibold text-muted-foreground">
            No Charts Available
          </h3>
        </div>
      )}

      <Grid container spacing={6}>
        {/* Render each chart group */}
        {chartGroups.map((group, groupIndex) => {
          return (
            <React.Fragment key={group.groupTitle}>
              <Grid 
                item
                {...(chartLayout === 'vertical' ? { xs: 12 } : (group.groupGridSize || { xs: 12 }))}
              >
                {/* Group Title */}
                <h2 className="text-lg font-semibold text-foreground mb-4 mt-4 first:mt-0">
                  {group.groupTitle}
                </h2>
                
                <Grid container spacing={6}>
                  {/* Charts in this group */}
                  {group.charts.map((chart, chartIndex) => {
                    const ChartComponent = chart.component;
                    return (
                      <Grid 
                        key={chart.fullscreenKey} 
                        item 
                        {...(chart.gridSize || getChartGridSize(chartLayout, group.charts.length, chartIndex))}
                      >
                        <ChartComponent
                          onFullscreenToggle={() => handleFullscreenToggle(chart.fullscreenKey)}
                          isFullscreen={false}
                          cellName={cellName}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            </React.Fragment>
          );
        })}
      </Grid>
    </div>
  );
};

export default CellKpisChartTab;
